import type { INotification } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const BASE = '/api/v1/notifications';

export interface NotificationFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  isRead?: boolean;
  sortOrder?: 'ASC' | 'DESC';
}

export interface PaginatedNotifications {
  items: INotification[];
  total: number;
}

export async function fetchNotifications(filters: NotificationFilters = {}): Promise<PaginatedNotifications> {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.search) params.set('search', filters.search);
  if (filters.type) params.set('type', filters.type);
  if (filters.isRead !== undefined) params.set('isRead', String(filters.isRead));
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

  const res = await apiFetch(`${BASE}?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch notifications: ${res.status}`));
  return res.json() as Promise<PaginatedNotifications>;
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await apiFetch(`${BASE}/unread-count`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch unread count'));
  const body = (await res.json()) as { count: number };
  return body.count;
}

export async function markNotificationRead(id: string): Promise<INotification> {
  const res = await apiFetch(`${BASE}/${id}/read`, { method: 'PATCH' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to mark notification as read'));
  return res.json() as Promise<INotification>;
}

export async function markNotificationUnread(id: string): Promise<INotification> {
  const res = await apiFetch(`${BASE}/${id}/unread`, { method: 'PATCH' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to mark notification as unread'));
  return res.json() as Promise<INotification>;
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await apiFetch(`${BASE}/mark-all-read`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to mark all notifications as read'));
}

export async function dismissNotification(id: string): Promise<void> {
  const res = await apiFetch(`${BASE}/${id}/dismiss`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to dismiss notification'));
}
